import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useProject } from '@/contexts/ProjectContext';
import { useApp } from '@/contexts/AppContext';
import { reconDocService, type ReconDocSummary, type ReconDocDetail } from '@/services/reconDocService';
import { queryKeys } from '@/lib/queryKeys';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, FileSearch, Trash2, List, Download } from 'lucide-react';
import { toast } from 'sonner';

// ── Slugify helper ────────────────────────────────────────────────────────

function slugify(text: string, seen: Map<string, number>): string {
  const base = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
  const count = seen.get(base) ?? 0;
  const id = count === 0 ? base : `${base}-${count}`;
  seen.set(base, count + 1);
  return id;
}

interface TocEntry {
  id: string;
  level: number;
  text: string;
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ReconTab() {
  const { projectId } = useProject();
  const { projectInfo } = useApp();
  const queryClient = useQueryClient();

  const { data: docs = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.reconDocs.all(projectId!),
    queryFn: () => reconDocService.list(projectId!),
    enabled: !!projectId && !isNaN(projectId),
  });

  const [selectedDoc, setSelectedDoc] = useState<ReconDocDetail | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [tocOpen, setTocOpen] = useState(false);
  const [toc, setToc] = useState<TocEntry[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Heading components: generate IDs from text via render-local seen map.
  // The seen map resets every render, so IDs are always consistent.
  const seen = new Map<string, number>();
  const headingComponents = {
    h1: ({ children }: { children?: React.ReactNode }) => {
      const id = slugify(String(children), seen);
      return <h1 id={id}>{children}</h1>;
    },
    h2: ({ children }: { children?: React.ReactNode }) => {
      const id = slugify(String(children), seen);
      return <h2 id={id}>{children}</h2>;
    },
    h3: ({ children }: { children?: React.ReactNode }) => {
      const id = slugify(String(children), seen);
      return <h3 id={id}>{children}</h3>;
    },
  };

  // Auto-select first doc when docs load and no doc is selected
  useEffect(() => {
    if (docs.length > 0 && !selectedDoc) {
      loadDoc(docs[0]);
    } else if (docs.length === 0 && selectedDoc) {
      setSelectedDoc(null);
    }
  }, [docs]);

  const loadDoc = async (summary: ReconDocSummary) => {
    if (!projectId) return;
    setLoadingDoc(true);
    try {
      const detail = await reconDocService.get(projectId, summary.id);
      setSelectedDoc(detail);
      setActiveHeading('');
    } catch {
      toast.error('Failed to load recon doc');
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleDelete = async (doc: ReconDocSummary) => {
    try {
      await reconDocService.remove(doc.id);
      toast.success(`"${doc.title}" deleted`);
      if (selectedDoc?.id === doc.id) setSelectedDoc(null);
      if (projectId) queryClient.invalidateQueries({ queryKey: queryKeys.reconDocs.all(projectId) });
    } catch {
      toast.error('Failed to delete recon doc');
    }
  };

  const handleDocSelect = (docId: string) => {
    const doc = docs.find((d) => String(d.id) === docId);
    if (doc) loadDoc(doc);
  };

  const handleDownload = () => {
    if (!selectedDoc) return;
    const baseName = projectInfo?.name ?? selectedDoc.title;
    const slug = baseName.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'recon';
    const blob = new Blob([selectedDoc.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-recon.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const scrollToHeading = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveHeading(id);
    setTocOpen(false);
  };

  // Build ToC from DOM + scroll-spy (single source of truth)
  useEffect(() => {
    if (!contentRef.current || !selectedDoc) {
      setToc([]);
      return;
    }

    // Small delay to ensure ReactMarkdown has finished rendering
    const timer = setTimeout(() => {
      if (!contentRef.current) return;
      const container = contentRef.current;
      const headings = container.querySelectorAll('h1, h2, h3');

      // Build ToC from actual rendered headings
      const entries: TocEntry[] = [];
      headings.forEach((h) => {
        if (h.id) {
          entries.push({
            id: h.id,
            level: parseInt(h.tagName[1], 10),
            text: h.textContent || '',
          });
        }
      });
      setToc(entries);

      // Set up IntersectionObserver for scroll-spy
      const observer = new IntersectionObserver(
        (observerEntries) => {
          for (const entry of observerEntries) {
            if (entry.isIntersecting) {
              setActiveHeading(entry.target.id);
              break;
            }
          }
        },
        { root: container, rootMargin: '-10% 0px -80% 0px', threshold: 0 }
      );
      headings.forEach((h) => observer.observe(h));

      // Store cleanup ref
      cleanupRef.current = () => observer.disconnect();
    }, 50);

    const cleanupRef = { current: () => {} };
    return () => {
      clearTimeout(timer);
      cleanupRef.current();
    };
  }, [selectedDoc]);

  if (!projectId) {
    return <div className="text-sm text-muted-foreground">No project selected.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading recon docs…
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <FileSearch className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No recon docs yet for this project.</p>
        <p className="text-xs text-muted-foreground/70">
          Use the MCP tool <code className="font-mono bg-muted px-1 rounded">push_recon</code> to add one.
        </p>
      </div>
    );
  }

  // Active heading label for the floating button
  const activeEntry = toc.find((e) => e.id === activeHeading);

  return (
    <div className="relative space-y-3">
      {/* ── Doc selector bar ──────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Select
          value={selectedDoc ? String(selectedDoc.id) : undefined}
          onValueChange={handleDocSelect}
        >
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Select a document…" />
          </SelectTrigger>
          <SelectContent>
            {docs.map((doc) => (
              <SelectItem key={doc.id} value={String(doc.id)}>
                {doc.title}
                {!doc.is_complete && (
                  <span
                    title={`${(doc.total_parts ?? 0) - doc.received_parts} part(s) missing`}
                    className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  >
                    Incomplete ({(doc.total_parts ?? 0) - doc.received_parts} missing)
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedDoc && (
          <>
            <span className="text-xs text-muted-foreground">
              {new Date(selectedDoc.updated_at).toLocaleDateString()}
            </span>
            {!selectedDoc.is_complete && (
              <span
                className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                title={`Missing parts: ${selectedDoc.missing_parts.join(', ')}`}
              >
                Incomplete — parts {selectedDoc.missing_parts.join(', ')} missing
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={handleDownload}
              title="Download as Markdown"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => {
                const doc = docs.find((d) => d.id === selectedDoc.id);
                if (doc) handleDelete(doc);
              }}
              title="Delete this document"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      {loadingDoc ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : selectedDoc ? (
        <Card>
          <CardContent className="pt-4 pb-6 px-6">
            <div
              ref={contentRef}
              className="prose prose-sm dark:prose-invert max-w-none overflow-auto"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={headingComponents}
              >
                {selectedDoc.content}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Floating ToC button ────────────────────────────────────────── */}
      {selectedDoc && toc.length > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <Popover open={tocOpen} onOpenChange={setTocOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="shadow-lg border-border/60 bg-background/95 backdrop-blur-sm gap-2 pr-3"
              >
                <List className="h-4 w-4" />
                {activeEntry ? (
                  <span className="max-w-[180px] truncate text-xs">
                    {activeEntry.text}
                  </span>
                ) : (
                  <span className="text-xs">Contents</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="end"
              className="w-72 max-h-[60vh] overflow-y-auto p-2"
            >
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 pb-2">
                Contents
              </p>
              <nav className="space-y-0.5">
                {toc.map((entry) => (
                  <button
                    key={entry.id}
                    onClick={() => scrollToHeading(entry.id)}
                    className={`block w-full text-left truncate rounded px-2 py-1 text-xs transition-colors
                      ${entry.level === 1 ? 'font-semibold' : entry.level === 2 ? 'pl-5' : 'pl-8 text-muted-foreground'}
                      ${activeHeading === entry.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground/80'}`}
                  >
                    {entry.text}
                  </button>
                ))}
              </nav>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
